import {
  ButtonGroupProps,
  ClickAwayListener,
  Grow,
  ButtonGroup as MuiButtonGroup,
  Popper,
  Tooltip,
  styled
} from '@mui/material'
import { Button, ButtonProps } from '@carto/meridian-ds/components'
import { ReactElement, ReactNode, useRef, useState } from 'react'
import { ArrowDown } from '@carto/meridian-ds/custom-icons'

const ArrowIcon = styled(ArrowDown, {
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  transform: `rotate(${isOpen ? '180' : '0'}deg)`
}))

const ButtonGroup = styled(MuiButtonGroup)(({ theme, size }) => ({
  '& .MuiButton-root:last-child': {
    padding: 0,
    minWidth: size === 'small' ? theme.spacing(3) : size === 'large' ? theme.spacing(6) : theme.spacing(4)
  }
}))

interface SplitButtonProps extends ButtonGroupProps {
  label: ReactNode | string
  icon?: ReactNode
  children: (close: () => void) => ReactElement
  tooltip?: ReactNode
  MainButtonProps?: ButtonProps
  SecondButtonProps?: ButtonProps
  onMainClick: () => void
  onSecondaryClick?: () => void
}

function SplitButton({
  label,
  disabled,
  icon,
  children,
  onMainClick,
  onSecondaryClick,
  tooltip: mainButtonTooltip,
  MainButtonProps,
  SecondButtonProps,
  ...buttonProps
}: SplitButtonProps) {
  const [open, setOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anchorRef: any = useRef<HTMLDivElement>()

  icon = icon ?? <ArrowIcon isOpen={open} />
  const handleSecondaryClick = () => {
    onSecondaryClick?.()
    setOpen(!open)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  const close = () => {
    setOpen(false)
  }

  return (
    <div ref={anchorRef}>
      <Tooltip title={mainButtonTooltip}>
        <ButtonGroup {...buttonProps}>
          <Button disabled={disabled} onClick={onMainClick} {...MainButtonProps}>
            {label}
          </Button>
          <Button disabled={disabled} onClick={handleSecondaryClick} {...SecondButtonProps}>
            {icon}
          </Button>
        </ButtonGroup>
      </Tooltip>
      {children && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          popperOptions={{ strategy: 'absolute' }}
          placement="bottom-end"
          transition
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: `right top`
              }}
            >
              <div>
                <ClickAwayListener onClickAway={handleClose}>
                  <div>{children(close)}</div>
                </ClickAwayListener>
              </div>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  )
}

export default SplitButton
